/**
 * DIS01-S 4.28 / DIS13-S: the epikrise Referral block must carry the referral's
 * Received time and its bidiagnoser that have a MedCom diagnose code. Bidiagnoser
 * without a code (uspecificeret) cannot be sent in Referral/ReferalAdditional;
 * Communicate omits them and Clinio warns instead of blocking.
 */

import { referralAdditionalDiagnoses } from '@/lib/referral/epikrise';

export type ReferralBlockCheck = { id: string; pass: boolean; detail: string };

export type ReferralBlockExpected = {
  received: { date: string; time: string };
  /** All bidiagnoser from medcom_details.additionalDiagnoses (coded and codeless). */
  additionalDiagnoses: ReadonlyArray<{ code: string | null; text: string | null }>;
};

export const REFERRAL_OMITTED_CODELESS_CHECK_ID = 'clinio.referral_omitted_codeless';

/** Check id from Clinio Communicate ref-preview (PR #60). */
export const COMMUNICATE_OMITTED_REFERRAL_DIAGNOSES_CHECK_ID = 'omittedReferralDiagnoses';

export function isCodedReferralAdditional(d: { code: string | null }): boolean {
  return Boolean(d.code?.trim());
}

export function codedReferralAdditionals(
  list: ReferralBlockExpected['additionalDiagnoses'],
): Array<{ code: string; text: string | null }> {
  return list.flatMap((d) => {
    const code = d.code?.trim();
    return code ? [{ code, text: d.text }] : [];
  });
}

function diagnoseLabel(d: { code: string | null; text: string | null }): string {
  const text = d.text?.trim();
  const code = d.code?.trim();
  if (text && code) return `${text} (${code})`;
  return text ?? code ?? '—';
}

/** Non-blocking warning when codeless bidiagnoser will be omitted from the Referral block. */
export function referralOmittedCodelessWarning(
  additionalDiagnoses: ReferralBlockExpected['additionalDiagnoses'],
): ReferralBlockCheck | null {
  const all = [...additionalDiagnoses];
  const codeless = all.filter((d) => !isCodedReferralAdditional(d));
  if (codeless.length === 0) return null;
  const codedCount = all.length - codeless.length;
  const omitted = codeless.map(diagnoseLabel).join(', ');
  return {
    id: REFERRAL_OMITTED_CODELESS_CHECK_ID,
    pass: true,
    detail:
      `Henvisningen har ${all.length} bidiagnoser, epikrisen medtager ${codedCount}. ` +
      `Udeladt: ${omitted}. MedComs skema kræver diagnosekode i Referral/ReferalAdditional.`,
  };
}

export function referralBlockExpectedFromDetails(
  details: Record<string, unknown> | null | undefined,
  received: { date: string; time: string },
): ReferralBlockExpected {
  return { received, additionalDiagnoses: referralAdditionalDiagnoses(details) };
}

function block(xml: string, name: string): string | null {
  const match = new RegExp(`<(?:\\w+:)?${name}\\b[^>]*>([\\s\\S]*?)</(?:\\w+:)?${name}>`).exec(xml);
  return match ? match[1] : null;
}

function value(xml: string, name: string): string | null {
  const match = new RegExp(`<(?:\\w+:)?${name}\\b[^>]*>([^<]*)</(?:\\w+:)?${name}>`).exec(xml);
  const v = match?.[1].trim() ?? '';
  return v.length > 0 ? v : null;
}

const digits = (v: string | null) => (v ?? '').replace(/[^0-9]/g, '');

/** MedCom Referral.xsd (2014/10/08): element name is ReferalAdditional, not Additional. */
const REFERRAL_ADDITIONAL_ELEMENT = 'ReferalAdditional';

function referralAdditionalsInXml(referralBlock: string): Array<{ code: string | null; text: string | null }> {
  const out: Array<{ code: string | null; text: string | null }> = [];
  const re = new RegExp(
    `<(?:\\w+:)?${REFERRAL_ADDITIONAL_ELEMENT}\\b[^>]*>([\\s\\S]*?)<\\/(?:\\w+:)?${REFERRAL_ADDITIONAL_ELEMENT}>`,
    'g',
  );
  for (const match of referralBlock.matchAll(re)) {
    out.push({ code: value(match[1], 'DiagnoseCode'), text: value(match[1], 'DiagnoseText') });
  }
  return out;
}

/**
 * Checks for the epikrise's Referral block. Empty when the XML has no Referral
 * block (nothing to compare); a check per value that is present.
 */
export function referralBlockChecks(xml: string, expected: ReferralBlockExpected): ReferralBlockCheck[] {
  const referral = block(xml, 'Referral');
  if (referral === null) return [];
  const checks: ReferralBlockCheck[] = [];
  const codedExpected = codedReferralAdditionals(expected.additionalDiagnoses);

  const received = block(referral, 'Received');
  if (received !== null) {
    const date = value(received, 'Date');
    const time = value(received, 'Time');
    const pass =
      digits(date) === digits(expected.received.date) &&
      digits(time).slice(0, 4) === digits(expected.received.time).slice(0, 4);
    checks.push({
      id: 'clinio.referral_received',
      pass,
      detail: pass
        ? `Henvisningens tidspunkt ${expected.received.date} ${expected.received.time}`
        : `Henvisningens tidspunkt er ${[date, time].filter(Boolean).join(' ') || 'tomt'} i epikrisen, men ${expected.received.date} ${expected.received.time} i henvisningen`,
    });
  }

  const inXml = referralAdditionalsInXml(referral);
  const xmlCodedCodes = inXml.flatMap((d) => (d.code?.trim() ? [d.code.trim()] : []));
  const passCount = inXml.length === codedExpected.length;
  checks.push({
    id: 'clinio.referral_additional_count',
    pass: passCount,
    detail: passCount
      ? `${inXml.length} bidiagnoser med diagnosekode som i henvisningen`
      : `Epikrisen har ${inXml.length} bidiagnoser med diagnosekode fra henvisningen, men henvisningen har ${codedExpected.length} med kode` +
        (expected.additionalDiagnoses.length > codedExpected.length
          ? ` (${expected.additionalDiagnoses.length - codedExpected.length} uden kode medtages ikke)`
          : ''),
  });

  const missingCoded = codedExpected.filter((d) => !xmlCodedCodes.includes(d.code));
  if (missingCoded.length > 0) {
    checks.push({
      id: 'clinio.referral_coded_missing',
      pass: false,
      detail: `Epikrisen mangler bidiagnoser med diagnosekode fra henvisningen: ${missingCoded.map(diagnoseLabel).join(', ')}`,
    });
  }

  const warning = referralOmittedCodelessWarning(expected.additionalDiagnoses);
  if (warning) checks.push(warning);

  return checks;
}

export function isReferralBlockWarningCheck(check: { id: string; pass: boolean; detail?: string }): boolean {
  if (!check.pass) return false;
  if (
    check.id === REFERRAL_OMITTED_CODELESS_CHECK_ID ||
    check.id === COMMUNICATE_OMITTED_REFERRAL_DIAGNOSES_CHECK_ID
  ) {
    return true;
  }
  const detail = check.detail?.trim() ?? '';
  return detail.includes('Udeladt:') && detail.includes('ReferalAdditional');
}
